import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-hot-toast";
import {
  createStaffLeave,
  applyStaffLeave,
} from "@/api/new-api/staff-management/leave/leave";
import useAuth from "@/hooks/useAuth";
import moment from "moment";

export function AddLeaveDialog({ isOpen, onClose, onSuccess, staffId }) {
  const { user } = useAuth();
  const [leaveType, setLeaveType] = useState("casual");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const calculateDays = () => {
    if (!fromDate || !toDate) return 0;
    const start = moment(fromDate);
    const end = moment(toDate);
    const diff = end.diff(start, "days") + 1;
    return diff > 0 ? diff : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) {
      toast.error("Please select both From and To dates.");
      return;
    }
    if (moment(toDate).isBefore(moment(fromDate))) {
      toast.error("To Date cannot be before From Date.");
      return;
    }

    const targetStaffId = staffId || user?.id;
    const days = calculateDays();

    const payload = {
      staff_id: targetStaffId,
      restaurant_id: user?.restaurant_id || 1,
      leave_type: leaveType,
      form_date: fromDate,
      to_date: toDate,
      days: days,
      reason: reason || `${leaveType.charAt(0).toUpperCase() + leaveType.slice(1)} Leave`,
      status: "pending",
    };

    setLoading(true);
    try {
      const res = await createStaffLeave(payload);

      if (
        res?.status === "success" ||
        res?.message?.toLowerCase()?.includes("success")
      ) {
        toast.success("Leave request submitted successfully!");
        const resData =
          res?.data?.leave || res?.data?.staff_leave || res?.data || {};
        const newLeaveItem = {
          ...payload,
          ...resData,
          leave_type: resData.leave_type || leaveType,
          type: resData.type || resData.leave_type || leaveType,
          days: resData.days || days,
          notes: resData.notes || resData.note || payload.notes,
          note: resData.notes || resData.note || payload.note,
          reason: resData.reason || resData.notes || resData.note || payload.reason,
          status: resData.status || "pending",
        };
        setLeaveType("casual");
        setFromDate("");
        setToDate("");
        setReason("");
        if (onSuccess) onSuccess(newLeaveItem);
        onClose();
      } else {
        toast.error(res?.message || "Failed to submit leave request.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-cblack4 text-white border-neutral-700">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-bold">
            Add Leave Request
          </DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Submit a new leave request for approval.
          </DialogDescription>
        </DialogHeader>
        <Separator className="bg-neutral-700 my-2" />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-300">
              Leave Type <span className="text-cred">*</span>
            </Label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger className="bg-cblack3 border-neutral-700 text-white">
                <SelectValue placeholder="Select leave type" />
              </SelectTrigger>
              <SelectContent className="bg-cblack4 text-white border-neutral-700">
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm text-neutral-300">
                From <span className="text-cred">*</span>
              </Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="bg-cblack3 border-neutral-700 text-white"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-neutral-300">
                To <span className="text-cred">*</span>
              </Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="bg-cblack3 border-neutral-700 text-white"
                required
              />
            </div>
          </div>

          {fromDate && toDate && (
            <div className="text-xs text-cyellow bg-cyellow/10 p-2 rounded border border-cyellow/30">
              Total Duration: <strong>{calculateDays()} days</strong>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-sm text-neutral-300">Reason / Note</Label>
            <Textarea
              rows={3}
              placeholder="Enter reason for leave..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-cblack3 border-neutral-700 text-white resize-none"
            />
          </div>

          <div className="flex gap-3 justify-end pt-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="bg-cblack6 text-neutral-300 border-neutral-700 hover:bg-cblack7"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-cyellow hover:bg-yellow-500 text-black font-semibold"
            >
              {loading ? "Submitting..." : "Submit Leave"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
